/**
 * <SchedulePicker> — the **only** UI we want owners to use when picking
 * a schedule, for both agent-cron (Automatic Runs) and function-cron
 * (CronBindingForm in functions-pane).
 *
 * Strict rule from the cron UX review: owners NEVER see / type / read a
 * 5-field cron expression. They pick a frequency preset + a time + a day
 * (when weekly), and we render the choice back as plain English via
 * `humanizeStructuredSchedule`.
 *
 * The picker is a controlled component over `StructuredSchedule`. The
 * caller owns:
 *   - persistence (agent-cron stores the shape natively; function-cron
 *     translates it through `cronExpressionFromStructured` at save time
 *     and back through `structuredFromCronExpression` for edits)
 *   - dirty-state tracking
 *
 * The picker owns the layout, the preset list, the time/day inputs, and
 * the detected-tz chip. Anything that requires reading or writing raw
 * cron stays out of this file.
 */

'use client';

import { useId } from 'react';

import type { StructuredSchedule, ScheduleFrequency, WeekDay } from '@/lib/agents/schedule-helpers';

export interface SchedulePickerProps {
  value: StructuredSchedule;
  onChange: (next: StructuredSchedule) => void;
  /** Owner-facing label for the frequency dropdown. Defaults to "How often". */
  frequencyLabel?: string;
  /** Owner-facing label for the time-of-day input. Defaults to "Starting at". */
  timeLabel?: string;
  /** Owner-facing label for the day-of-week select. Defaults to "On". */
  dayLabel?: string;
  /** Render an hourly-frequency cost warning when true. */
  showHourlyWarning?: boolean;
  /** Hourly warning copy. Defaults to a generic line. */
  hourlyWarning?: string;
  /** Disable all inputs (e.g. when the parent toggle is off). */
  disabled?: boolean;
  /** Optional translator for i18n keys. Strings already in props win. */
  t?: (key: string) => string;
}

const FREQUENCY_OPTIONS: Array<{ value: ScheduleFrequency; labelKey: string; fallback: string }> = [
  { value: 'hourly', labelKey: 'agents.schedEveryHour', fallback: 'Every hour' },
  { value: 'every_6h', labelKey: 'agents.schedEvery6h', fallback: 'Every 6 hours' },
  { value: 'every_12h', labelKey: 'agents.schedEvery12h', fallback: 'Every 12 hours' },
  { value: 'daily', labelKey: 'agents.schedDaily', fallback: 'Daily' },
  { value: 'weekly', labelKey: 'agents.schedWeekly', fallback: 'Weekly' },
];

const DAY_OPTIONS: Array<{ value: WeekDay; label: string }> = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

function detectTimeZone(): string {
  if (typeof Intl === 'undefined') return 'UTC';
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function SchedulePicker({
  value,
  onChange,
  frequencyLabel,
  timeLabel,
  dayLabel,
  showHourlyWarning = true,
  hourlyWarning,
  disabled,
  t,
}: SchedulePickerProps) {
  const tz = value.timeZone ?? detectTimeZone();
  const showTimeAndDay = value.frequency === 'daily' || value.frequency === 'weekly';
  const ids = {
    freq: useId(),
    time: useId(),
    day: useId(),
  };

  const tr = (key: string, fallback: string): string => {
    if (!t) return fallback;
    const out = t(key);
    return out === key ? fallback : out;
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={ids.freq} className="block text-sm font-medium mb-1.5">
          {frequencyLabel ?? tr('agents.howOften', 'How often')}
        </label>
        <select
          id={ids.freq}
          value={value.frequency}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...value,
              frequency: e.target.value as ScheduleFrequency,
              timeZone: value.timeZone ?? tz,
            })
          }
          className="input w-full"
        >
          {FREQUENCY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {tr(opt.labelKey, opt.fallback)}
            </option>
          ))}
        </select>
      </div>

      {showTimeAndDay && (
        <div className="grid grid-cols-1 @md/main:grid-cols-2 gap-3">
          {value.frequency === 'weekly' && (
            <div>
              <label htmlFor={ids.day} className="block text-sm font-medium mb-1.5">
                {dayLabel ?? tr('agents.onDay', 'On')}
              </label>
              <select
                id={ids.day}
                value={value.preferredDay ?? 1}
                disabled={disabled}
                onChange={(e) =>
                  onChange({ ...value, preferredDay: Number(e.target.value) as WeekDay })
                }
                className="input w-full"
              >
                {DAY_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor={ids.time} className="block text-sm font-medium mb-1.5">
              {timeLabel ?? tr('agents.startingAt', 'Starting at')}
            </label>
            <div className="flex items-center gap-3">
              <input
                id={ids.time}
                type="time"
                value={value.preferredTime ?? '09:00'}
                disabled={disabled}
                onChange={(e) =>
                  onChange({ ...value, preferredTime: e.target.value, timeZone: value.timeZone ?? tz })
                }
                className="input"
              />
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-muted text-xs text-muted-foreground"
                title="Schedule resolves relative to this timezone"
              >
                {tz}
              </span>
            </div>
          </div>
        </div>
      )}

      {showHourlyWarning && value.frequency === 'hourly' && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          {hourlyWarning ?? tr('agents.schedHourlyWarning', '~720 runs/month. Each run uses tokens like a normal conversation.')}
        </p>
      )}
    </div>
  );
}
